import { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { scaleOrdinal } from 'd3-scale';
import { schemeSet2 } from 'd3-scale-chromatic';
import { darken, lighten } from '@mui/material';
import { TypeGroup } from './types';
import { CustomBarItem } from './CustomBarItem';

const PATTERNS = [
    { 'type': 'patternDots', 'size': 0, 'padding': 4, 'stagger': false }, 
    { 'type': 'patternSquares', 'size': 8, 'padding': 0, 'stagger': true }, 
    { 'type': 'patternDots', 'size': 3, 'padding': 5, 'stagger': false }, 
    { 'type': 'patternLines', 'spacing': 6, 'rotation': 45, 'lineWidth': 3 }, 
    { 'type': 'patternDots', 'size': 10, 'padding': 6, 'stagger': false }, 
    { 'type': 'patternSquares', 'size': 4, 'padding': 0, 'stagger': true }, 
    { 'type': 'patternLines', 'spacing': 8, 'rotation': 135, 'lineWidth': 4 }, 
    { 'type': 'patternLines', 'spacing': 10, 'rotation': 135, 'lineWidth': 2 },
];

interface BarChartProps {
  data: any[];
  typeGroups: TypeGroup[];
  legend: string;
}

const BarChart = ({ data, typeGroups, legend }: BarChartProps) => {

    const series = useMemo(() => {
        const keySet: Set<string> = new Set();
        for (const tGroup of typeGroups) {
            tGroup.subtypes.forEach(s => keySet.add(s));
        }
        return Array.from(keySet);
    }, [ typeGroups ]);

    const colorScale = useMemo(() => scaleOrdinal(schemeSet2).domain(typeGroups.map(sg => sg.category)), [ typeGroups ]);
    const patternScale = useMemo(() => scaleOrdinal(PATTERNS).domain(typeGroups.map(sg => sg.category)), [ typeGroups ]);

    const serieColors = useMemo(() => {
        const colors = {};
        for (const sGroup of typeGroups) {
            const downShift = Math.floor(sGroup.subtypes.length / 5);
            for (const [ind, struct] of sGroup.subtypes.entries()) {
                const shiftedInd = ind - downShift;
                if (shiftedInd < 0) {
                    // lighten the overallStructure color based on the structures index, without
                    // going over 70% of the way to white
                    colors[struct] = lighten(colorScale(sGroup.category), 0.7 * Math.abs(shiftedInd) / (downShift + 1));
                } else {
                    // darken the overallStructure color based on the structures index, without
                    // going over 70% of the way to black
                    colors[struct] = darken(colorScale(sGroup.category), 0.7 * shiftedInd / (sGroup.subtypes.length - downShift));
                }
            }
        }
        return colors;
    }, [ typeGroups, colorScale ]);
    
    const serieDefsAndFill = useMemo(() => {
        const defsAndFill: { defs: any[], fill: any[] } = { defs: [], fill: [] };
        for (const sGroup of typeGroups) {
            const downShift = Math.floor(sGroup.subtypes.length / 5);
            for (const [ind, struct] of sGroup.subtypes.entries()) {
                const shiftedInd = ind - downShift;

                let bgColor;
                let fgColor;
                if (shiftedInd < 0) {
                    // lighten the overallStructure color based on the structures index, without
                    // going over 70% of the way to white
                    bgColor = lighten(colorScale(sGroup.category), 0.7 * Math.abs(shiftedInd) / (downShift + 1));
                    fgColor = darken(bgColor, 0.2);
                } else {
                    // darken the overallStructure color based on the structures index, without
                    // going over 70% of the way to black
                    bgColor = darken(colorScale(sGroup.category), 0.7 * shiftedInd / (sGroup.subtypes.length - downShift));
                    fgColor = darken(bgColor, 0.2);
                }

                defsAndFill.defs.push({
                    ...patternScale(sGroup.category),
                    "id": struct.replace(/[^a-zA-Z0-9]/g, '-'),
                    "background": bgColor,
                    "color": fgColor
                });
                defsAndFill.fill.push({
                    match: { id: struct },
                    id: struct.replace(/[^a-zA-Z0-9]/g, '-')
                });
            }
        }
        return defsAndFill;
    }, [ typeGroups, colorScale ]);

    const legends = useMemo(() => {
        let items: any[] = [];
        for (const fill of serieDefsAndFill.fill) {
            items.push({
                id: fill.id,
                label: fill.match.id,
                color: `url(#${fill.id})`
            });
        }
        return items;
    }, [ serieDefsAndFill ]);

    const largestGroupLength = data.reduce((max, item) => {
      return item.name?.length > max ? item.name?.length : max;
    }, 0);
  
    const offsetConst = Math.log(largestGroupLength) / Math.log(2.1);
    const yAxisOffset = offsetConst * 50;
    const yAxisLabelOffset = -offsetConst * 45;
    
    const longestTypeLength = series.reduce((max, item) => {
        return item.length > max ? item.length : max;
    }, 0);
  
    const legendOffsetConst = Math.log(longestTypeLength) / Math.log(2.1);
    const legendYAxisOffset = legendOffsetConst * 50 + 30; // account for colored square
    const rightMargin = legendYAxisOffset +  15; // add a small gap between chart and legend

    return (
        <ResponsiveBar
            barComponent={CustomBarItem}
            data={data as any}
            keys={series}
            colors={({ id }) => serieColors[id] || 'rgba(3, 127, 176, 1)'}
            defs={serieDefsAndFill.defs}
            fill={serieDefsAndFill.fill}
            indexBy='name'
            layout='horizontal'
            enableLabel={false}
            motionConfig="gentle"
            padding={0.25}
            margin={{ top: 20, right: rightMargin, bottom: 60, left: yAxisOffset }}
            enableGridX={true}
            enableGridY={false}
            axisRight={null}
            layers={[
                "grid",
                "axes",
                "bars",
                "annotations",
                "markers",
                "legends"
            ]}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              legend: legend,
              legendPosition: 'middle',
              legendOffset: 48,
              format: (v => `${v}`)
            }}
            axisLeft={{
              legendOffset: yAxisLabelOffset,
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Project',
              legendPosition: 'middle',
              format: (i => `${i}`)
            }}
            legends={[
                {
                    dataFrom: 'keys',
                    data: legends,
                    anchor: 'top-right',
                    direction: 'column',
                    justify: false,
                    translateX: rightMargin,
                    translateY: 0,
                    itemsSpacing: 4,
                    itemWidth: legendYAxisOffset,
                    itemHeight: 16,
                    itemDirection: 'left-to-right',
                    itemOpacity: 1,
                    symbolSize: 16
                }
            ]}
        />
    );
};

export default BarChart;